import type { DraftSnapshot } from "@filosign/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Hex } from "viem";
import { useFilosignContext } from "../../context/useFilosignContext";
import {
	decryptDraftDekFromColdShare,
	decryptDraftDekFromWarmShare,
	decryptDraftDocument,
	decryptDraftSnapshot,
} from "../../lib/draft-crypto";
import { useFilosignRpc } from "../../lib/use-filosign-rpc";
import { walletAccountAddress } from "../../utils/evm";
import { getSessionSeed } from "../auth/session-seed";

export const DRAFT_REVIEW_MISSING_CRYPTO_SEED =
	"DRAFT_REVIEW_MISSING_CRYPTO_SEED" as const;

const DRAFT_REVIEW_DECRYPT_FAILED_MESSAGE =
	"Could not decrypt this draft. Confirm you're signed in with the invited account and complete encryption unlock.";

export function normalizeDraftReviewDecryptError(error: unknown): Error {
	if (
		error instanceof Error &&
		error.message === DRAFT_REVIEW_MISSING_CRYPTO_SEED
	) {
		return error;
	}
	if (
		error instanceof Error &&
		(error.message === "No unlocked key seed found" ||
			error.message.includes("No unlocked key seed"))
	) {
		return new Error(DRAFT_REVIEW_MISSING_CRYPTO_SEED);
	}
	if (error instanceof DOMException) {
		if (error.name === "OperationError" || error.name === "NotSupportedError") {
			return new Error(DRAFT_REVIEW_DECRYPT_FAILED_MESSAGE);
		}
	}
	if (error instanceof Error) {
		if (
			error.name === "OperationError" ||
			error.message.includes("operation-specific reason")
		) {
			return new Error(DRAFT_REVIEW_DECRYPT_FAILED_MESSAGE);
		}
		return error;
	}
	return new Error(DRAFT_REVIEW_DECRYPT_FAILED_MESSAGE);
}

async function withDraftReviewDecryptErrors<T>(
	fn: () => Promise<T>,
): Promise<T> {
	try {
		return await fn();
	} catch (error) {
		throw normalizeDraftReviewDecryptError(error);
	}
}

export function useDraftReviewByToken(inviteToken: string | undefined) {
	const { rpc } = useFilosignRpc();
	const token = inviteToken?.trim();

	return useQuery({
		queryKey: ["filosign", "drafts", "reviewByToken", token ?? ""],
		queryFn: () => {
			if (!token) throw new Error("token required");
			return rpc.drafts.reviewByToken({ inviteToken: token });
		},
		enabled: Boolean(token),
	});
}

export function useDecryptDraftReviewCold() {
	return useMutation({
		mutationFn: async (args: {
			draftId: string;
			inviteToken: string;
			phrase: string;
			wrappedDek: Hex;
			snapshotDownloadUrl: string;
			documents: {
				docId: string;
				name: string;
				mimeType: string;
				downloadUrl: string;
			}[];
		}) => {
			const dek = await decryptDraftDekFromColdShare({
				wrappedDek: args.wrappedDek,
				phrase: args.phrase,
				draftId: args.draftId,
				inviteToken: args.inviteToken,
			});

			const snapRes = await fetch(args.snapshotDownloadUrl);
			if (!snapRes.ok) throw new Error("Failed to download snapshot");
			const snapshot = await decryptDraftSnapshot({
				dek,
				draftId: args.draftId,
				ciphertext: new Uint8Array(await snapRes.arrayBuffer()),
			});

			const documents: {
				id: string;
				name: string;
				type: string;
				bytes: Uint8Array;
			}[] = [];
			for (const doc of args.documents) {
				const dl = await fetch(doc.downloadUrl);
				if (!dl.ok) throw new Error(`Failed to download ${doc.name}`);
				const bytes = await decryptDraftDocument({
					dek,
					draftId: args.draftId,
					docId: doc.docId,
					ciphertext: new Uint8Array(await dl.arrayBuffer()),
				});
				documents.push({
					id: doc.docId,
					name: doc.name,
					type: doc.mimeType,
					bytes,
				});
			}

			return { snapshot, documents, reviewDek: dek } satisfies {
				snapshot: DraftSnapshot;
				documents: {
					id: string;
					name: string;
					type: string;
					bytes: Uint8Array;
				}[];
				reviewDek: Uint8Array;
			};
		},
	});
}

export function useDecryptDraftReviewWarm() {
	const { wallet } = useFilosignContext();
	const { rpc, isAuthed } = useFilosignRpc();

	return useMutation({
		mutationFn: async (args: { inviteToken: string }) =>
			withDraftReviewDecryptErrors(async () => {
				if (!wallet?.account || !isAuthed) {
					throw new Error("Sign in to open this draft");
				}
				const walletAddress = walletAccountAddress(wallet.account);
				if (!getSessionSeed(walletAddress)) {
					throw new Error(DRAFT_REVIEW_MISSING_CRYPTO_SEED);
				}

				const head = await rpc.drafts.reviewForWallet({
					inviteToken: args.inviteToken,
				});

				const dek = await decryptDraftDekFromWarmShare({
					kemCiphertext: head.kemCiphertext,
					encryptedDek: head.encryptedDek,
					draftId: head.draftId,
					inviteToken: args.inviteToken,
					wallet: walletAddress,
				});

				const snapRes = await fetch(head.snapshotDownloadUrl);
				if (!snapRes.ok) throw new Error("Failed to download snapshot");
				const snapshot = await decryptDraftSnapshot({
					dek,
					draftId: head.draftId,
					ciphertext: new Uint8Array(await snapRes.arrayBuffer()),
				});

				const documents: {
					id: string;
					name: string;
					type: string;
					bytes: Uint8Array;
				}[] = [];
				for (const doc of head.documents) {
					const dl = await fetch(doc.downloadUrl);
					if (!dl.ok) throw new Error(`Failed to download ${doc.name}`);
					const bytes = await decryptDraftDocument({
						dek,
						draftId: head.draftId,
						docId: doc.docId,
						ciphertext: new Uint8Array(await dl.arrayBuffer()),
					});
					documents.push({
						id: doc.docId,
						name: doc.name,
						type: doc.mimeType,
						bytes,
					});
				}

				return { snapshot, documents, title: head.title, reviewDek: dek };
			}),
	});
}
